/**
 * useControlPointDrag Hook
 * Consolidates drag state management and window event listener setup for control point overlays
 *
 * Provides:
 * - State management (dragState, snapTarget, playerSnapTarget)
 * - Ref synchronization to avoid stale closures
 * - Window pointerup event listener management
 * - Cleanup utilities
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { PlayerSnapTarget, SnapTarget } from '@/utils/Drawing.utils'

/**
 * Generic drag state - components can extend this
 */
export interface BaseDragState {
	pointId: string
	drawingId?: string

	[key: string]: any
}

interface UseControlPointDragOptions<T extends BaseDragState> {
	/**
	 * Callback invoked on window pointerup
	 * Receives current drag state and snap targets from refs (not stale closures)
	 */
	onPointerUp: (params: {
		dragState: T | null
		snapTarget: SnapTarget | null
		playerSnapTarget: PlayerSnapTarget | null
	}) => void
}

interface UseControlPointDragReturn<T extends BaseDragState> {
	// State
	dragState: T | null
	snapTarget: SnapTarget | null
	playerSnapTarget: PlayerSnapTarget | null

	// State setters
	setDragState: (state: T | null) => void
	setSnapTarget: (target: SnapTarget | null) => void
	setPlayerSnapTarget: (target: PlayerSnapTarget | null) => void

	// Refs (for avoiding stale closures)
	dragStateRef: React.MutableRefObject<T | null>
	snapTargetRef: React.MutableRefObject<SnapTarget | null>
	playerSnapTargetRef: React.MutableRefObject<PlayerSnapTarget | null>

	// Cleanup utility
	cleanup: () => void
}

/**
 * Manages control point drag state with refs to avoid stale closures.
 * Sets up a stable window pointerup listener that reads latest values from refs.
 *
 * The window event listener is added once and never recreated, preventing
 * performance issues from frequent add/remove cycles. All drag state is
 * synchronized to refs to prevent stale closure bugs.
 *
 * @template T - Drag state type (must extend BaseDragState)
 * @param options - Configuration options
 * @param options.onPointerUp - Callback invoked on window pointerup with latest ref values
 * @returns Object with drag state, setters, refs, and cleanup utility
 *
 * @example
 * ```tsx
 * const {
 *   dragState,
 *   snapTarget,
 *   setDragState,
 *   setSnapTarget,
 *   cleanup
 * } = useControlPointDrag({
 *   onPointerUp: ({ dragState, snapTarget }) => {
 *     if (dragState && snapTarget) {
 *       // Handle snap
 *     }
 *     cleanup()
 *   }
 * })
 * ```
 */
export function useControlPointDrag<T extends BaseDragState>({
																 onPointerUp,
															 }: UseControlPointDragOptions<T>): UseControlPointDragReturn<T> {
	// State
	const [dragState, setDragState] = useState<T | null>(null)
	const [snapTarget, setSnapTarget] = useState<SnapTarget | null>(null)
	const [playerSnapTarget, setPlayerSnapTarget] = useState<PlayerSnapTarget | null>(null)

	// Refs to avoid stale closures in handlePointerUp
	const dragStateRef = useRef<T | null>(null)
	const snapTargetRef = useRef<SnapTarget | null>(null)
	const playerSnapTargetRef = useRef<PlayerSnapTarget | null>(null)
	const onPointerUpRef = useRef(onPointerUp)

	// Sync refs when state changes
	useEffect(() => {
		dragStateRef.current = dragState
	}, [dragState])

	useEffect(() => {
		snapTargetRef.current = snapTarget
	}, [snapTarget])

	useEffect(() => {
		playerSnapTargetRef.current = playerSnapTarget
	}, [playerSnapTarget])

	// Keep onPointerUp ref in sync
	useEffect(() => {
		onPointerUpRef.current = onPointerUp
	}, [onPointerUp])

	// Cleanup utility
	const cleanup = useCallback(() => {
		setDragState(null)
		setSnapTarget(null)
		setPlayerSnapTarget(null)
		dragStateRef.current = null
		snapTargetRef.current = null
		playerSnapTargetRef.current = null
	}, [])

	// Handle window pointerup
	// Use ref for callback to avoid recreating on every render
	const handlePointerUp = useCallback(() => {
		onPointerUpRef.current({
			dragState: dragStateRef.current,
			snapTarget: snapTargetRef.current,
			playerSnapTarget: playerSnapTargetRef.current,
		})
	}, []) // Empty deps - callback is stable, reads latest values from refs

	// Set up window event listener
	useEffect(() => {
		window.addEventListener('pointerup', handlePointerUp)
		return () => window.removeEventListener('pointerup', handlePointerUp)
	}, [handlePointerUp])

	return {
		dragState,
		snapTarget,
		playerSnapTarget,
		setDragState,
		setSnapTarget,
		setPlayerSnapTarget,
		dragStateRef,
		snapTargetRef,
		playerSnapTargetRef,
		cleanup,
	}
}
